import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Chip, makeStyles, Paper } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import AppColor from "utilities/AppColor";
import AppButton from "components/CustomButtons/AppButton";
import {
  fetchClientDetail,
  resendIntakeLink,
} from "store/slices/client";
import { getAuthInfo } from "store/slices/auth";
import { showToast } from "store/slices/toast";
import ParticipantUserProfile from "./ParticipantUserProfile";
import ParticipantProgressNote from "./ParticipantProgressNote";
import ParticipantMedication from "./ParticipantMedication";
import RiskConsentTab from "./tabs/RiskConsentTab";
import DocumentClientTab from "./tabs/DocumentClientTab";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import ComponentLoading from "components/Common/ComponentLoading";
import GoalTrackerTab from "./tabs/GoalTrackerTab";
import { fetchNoteQuestions, getNoteQuestions } from "store/slices/processNote";
import SendIntakeLinkModal from "./SendIntakeLinkModal";
import AppDialog from "components/Dialog/AppDialog";
import { activateApprovalClient } from "store/slices/client";

const useStyles = makeStyles(() => ({
  page: {
    padding: "16px",
  },
  backdrop: {
    zIndex: 9999,
    color: "#fff",
  },
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 6,
  },
  breadcrumbLink: {
    color: "#6B7280",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  breadcrumbSep: { color: "#D1D5DB" },
  breadcrumbCurrent: { color: "#111827" },
  pageTitle: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#2dd4bf",
    marginBottom: 16,
  },
  headerCard: {
    background: "#fff",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    padding: "16px 20px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  headerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "#CCFBF1",
    color: "#0D9488",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: 18,
    flexShrink: 0,
  },
  participantName: {
    fontSize: 18,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 4,
  },
  subInfo: {
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 8,
  },
  chipRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  headerRight: {
    display: "flex",
    gap: 8,
    flexShrink: 0,
    alignItems: "center",
  },
  card: {
    background: "#fff",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    overflow: "hidden",
  },
  tabsContainer: {
    display: "flex",
    borderBottom: "1px solid #E5E7EB",
    overflowX: "auto",
    overflowY: "hidden",
  },
  tab: {
    padding: "10px 16px",
    fontSize: 13,
    color: "#6B7280",
    cursor: "pointer",
    borderBottom: "2.5px solid transparent",
    marginBottom: -1,
    whiteSpace: "nowrap",
    "&:hover": { color: "#111827" },
  },
  tabActive: {
    color: "#0D9488",
    borderBottomColor: "#2dd4bf",
    fontWeight: 600,
  },
  tabContent: {
    padding: 20,
  },
}));

const getSilChipStyle = (level) => {
  switch (level) {
    case "HIGH_INTENSITY": return { backgroundColor: "#EDE9FE", color: "#7C3AED" };
    case "STANDARD": return { backgroundColor: AppColor.mainLight, color: AppColor.green };
    case "COMPLEX": return { backgroundColor: "#FEF3C7", color: "#D97706" };
    case "RESPITE": return { backgroundColor: "#E0F2FE", color: "#0369A1" };
    default: return { backgroundColor: "#F3F4F6", color: "#6B7280" };
  }
};

const ClientDetailTabs = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const authInfo = useSelector(getAuthInfo);
  const classes = useStyles();

  const [clientDetail, setClientDetail] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showConfirmIntakeModal, setShowConfirmIntakeModal] = useState(false);
  const [showResendConfirmModal, setShowResendConfirmModal] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  const questions = useSelector(getNoteQuestions);

  const loadClientDetail = async (isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    }
    try {
      const result = await dispatch(fetchClientDetail({ clientId: id, companyId: authInfo.companyId }));
      if (result.payload) {
        setClientDetail(result.payload);
      }
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    if (id) loadClientDetail(true);
  }, [id]);

  useEffect(() => {
    if (authInfo?.companyId) {
      dispatch(fetchNoteQuestions({ companyId: authInfo.companyId, clientId: id }));
    }
  }, [authInfo?.companyId, dispatch]);

  const { client = {} } = clientDetail;
  const { firstName = "", lastName = "", name, ndisPlanNumber, billingDetail, approvalStatus, recipientEmail } = client;
  
  const silLevel = client.silLevel || client.sil_level;
  const fullName = `${firstName} ${lastName}`.trim() || name || "";
  const initials = fullName.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const isValidToActivate = () => {
    if (!client) return false;
    const {
      clientType,
      ndisPlanNumber,
      address1,
      postalCode,
      billingDetail,
      planManagerEmail,
      billingResponse,
    } = client;

    if (!clientType || !ndisPlanNumber || !address1 || !postalCode || !billingDetail || !planManagerEmail || !billingResponse?.email) {
      return false;
    }
    return true;
  };

  const handleConfirmIntake = async () => {
    setConfirmLoading(true);
    try {
      const result = await dispatch(activateApprovalClient(id));
      if (result.payload) {
        await loadClientDetail();
      }
    } catch (e) {
      console.error(e);
      dispatch(showToast({ message: "Failed to activate participant", type: "error" }));
    } finally {
      setConfirmLoading(false);
      setShowConfirmIntakeModal(false);
    }
  };
  const handleClickSendIntakeLink = () => {
    if (!recipientEmail) {
      dispatch(showToast({ message: t("text.send_intake_link_tooltip"), type: "warning" }));
    } else {
      setShowResendConfirmModal(true);
    }
  };

  const handleResendIntakeLink = async () => {
    setResendLoading(true);
    try {
      const res = await dispatch(resendIntakeLink(client.id));
      if (res.payload) {
        setShowResendConfirmModal(false);
        await loadClientDetail();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setResendLoading(false);
    }
  };

  const formatBilling = (b) => {
    switch (b) {
      case "NDIA": return "Agency managed";
      case "PLAN_MANAGER": return "Plan managed";
      case "SELF": return "Self managed";
      default: return "";
    }
  };

  const subInfoParts = [
    ndisPlanNumber && `NDIS ${ndisPlanNumber}`,
    formatBilling(billingDetail),
    client.houseName,
    client.roomName,
  ].filter(Boolean);

  const tabs = [
    {
      label: t("label.user_profile"),
      component: (
        <ParticipantUserProfile
          clientDetail={client}
          loadClientDetail={loadClientDetail}
          companyId={authInfo.companyId}
        />
      ),
    },
    { label: t("label.progressNote"), component: <ParticipantProgressNote /> },
    { label: t("label.goal_tracker"), component: <GoalTrackerTab questions={questions} /> },
    { label: t("label.funding"), component: <Box py={4} textAlign="center" color="#9CA3AF">{t("label.coming_soon")}</Box> },
    { label: t("label.medication"), component: <ParticipantMedication companyId={authInfo.companyId} /> },
    { label: t("label.risk_consent"), component: <RiskConsentTab participantName={fullName} /> },
    { label: t("label.family_reports"), component: <Box py={4} textAlign="center" color="#9CA3AF">{t("label.coming_soon")}</Box> },
    { label: t("label.documents"), component: <DocumentClientTab client={client} reloadClientDetail={loadClientDetail} /> },
  ];

  return (
    <Box className={classes.page}>
      {/* Breadcrumb */}
      <Box className={classes.breadcrumb}>
        <Link to="/admin/participants" className={classes.breadcrumbLink}>
          {t("label.participants")}
        </Link>
        <span className={classes.breadcrumbSep}>›</span>
        <span className={classes.breadcrumbCurrent}>{fullName}</span>
      </Box>

      {/* Header card */}
      <Box className={classes.headerCard}>
        <Box className={classes.headerLeft}>
          <Box className={classes.avatar}>{initials || "P"}</Box>
          <Box>
            <Box className={classes.participantName}>{fullName}</Box>
            {subInfoParts.length > 0 && (
              <Box className={classes.subInfo}>{subInfoParts.join(" · ")}</Box>
            )}
            <Box className={classes.chipRow}>
              {silLevel && (
                <Chip
                  size="small"
                  label={t(`sil_level.${silLevel}`)}
                  style={{ ...getSilChipStyle(silLevel), fontWeight: 600, fontSize: 12 }}
                />
              )}
              {approvalStatus && (
                <Chip
                  size="small"
                  label={t(`approval_status.${approvalStatus}`)}
                  style={{
                    backgroundColor: AppColor.mainLight,
                    color: AppColor.green,
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          {approvalStatus !== "ACTIVE" && (
            <AppButton
              label={t("button.active")}
              mode="outlined"
              buttonColor="#2dd4bf"
              style={{ textTransform: "none", backgroundColor: "white" }}
              // disabled={approvalStatus !== "WAITING_FOR_APPROVAL" || isExpired || confirmLoading}
              disabled={confirmLoading}
              isLoading={confirmLoading}
              onClick={() => {
                if (!isValidToActivate()) {
                  dispatch(
                    showToast({
                      type: "warning",
                      message: t("customUI.activeParticipantRequireProfile"),
                    })
                  );
                } else {
                  setShowConfirmIntakeModal(true);
                }
              }}
            />
          )}
          <AppButton
            label={t("label.send_intake_link")}
            mode="outlined"
            buttonColor="#2dd4bf"
            isLoading={resendLoading}
            startIcon={<i className="fas fa-paper-plane" style={{ fontSize: 14 }} />}
            style={{ textTransform: "none", backgroundColor: "white" }}
            onClick={handleClickSendIntakeLink}
          />
        </Box>
      </Box>
      {/* Code new:
      {approvalStatus !== "ACTIVE" && (
            <AppButton
              label={isExpired ? "Expired" : "Confirm Intake Link"}
              mode="contained"
              buttonColor={AppColor.mainColor}
              startIcon={<i className="fas fa-check" style={{ fontSize: 14 }} />}
              style={{ textTransform: "none", color: "white", marginRight: 10 }}
              disabled={approvalStatus !== "WAITING_FOR_APPROVAL" || isExpired || confirmLoading}
              isLoading={confirmLoading}
              onClick={() => setShowConfirmIntakeModal(true)}
            />
          )}
          {approvalStatus !== "ACTIVE" && (
            <AppButton
              label="Resend Intake Link"
              mode="outlined"
              buttonColor="#2dd4bf"
              startIcon={<i className="fas fa-paper-plane" style={{ fontSize: 14 }} />}
              style={{ textTransform: "none", backgroundColor: "white" }}
              isLoading={resendLoading}
              onClick={() => setShowResendConfirmModal(true)}
            />
          )}
      */}

      {/* Tabs + content */}
      <Paper className={classes.card}>
        <Box className={classes.tabsContainer}>
          {tabs.map((tab, index) => (
            <Box
              key={index}
              className={`${classes.tab} ${activeTab === index ? classes.tabActive : ""}`}
              onClick={() => setActiveTab(index)}
            >
              {tab.label}
            </Box>
          ))}
        </Box>
        <Box className={classes.tabContent}>{tabs[activeTab].component}</Box>
      </Paper>


      {/* <Backdrop className={classes.backdrop} open={confirmLoading || initialLoading}>
        <ComponentLoading />
      </Backdrop> */}
      {
        // (confirmLoading || initialLoading) && <FullScreenLoading/>
      }
      <AppDialog
        open={confirmLoading || initialLoading}
        noTitle
        noCloseButton
        disableEscapeKeyDown
      >
        <Box my={3}>
          <ComponentLoading />
        </Box>
      </AppDialog>

      {showIntakeModal && (
        <SendIntakeLinkModal
          open={showIntakeModal}
          onClose={() => setShowIntakeModal(false)}
          onSuccess={loadClientDetail}
        />
      )}

      <AppConfirmDialog
        open={showConfirmIntakeModal}
        onConfirm={handleConfirmIntake}
        onClose={() => setShowConfirmIntakeModal(false)}
        title={t("title.active_participant")}
        content={t("text.active_participant")}
      />

      <AppConfirmDialog
        open={showResendConfirmModal}
        onConfirm={handleResendIntakeLink}
        onClose={() => setShowResendConfirmModal(false)}
        title="Resend Intake Link"
        content="Are you sure you want to generate a new token and resend the intake link email to this client?"
      />
    </Box>
  );
};

export default ClientDetailTabs;
